import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Mail, MessageCircle } from "lucide-react";
import { CONTACT, PLANS, waLink, tgLink } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", plan: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Por favor completa todos los campos obligatorios.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("¡Mensaje enviado! Te contactaremos muy pronto.");
      setForm({ name: "", email: "", plan: "", message: "" });
    } catch {
      toast.error("No se pudo enviar. Intenta por WhatsApp o Telegram.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-bp-surface border border-bp-border px-4 py-3 text-sm text-white placeholder:text-[#737373] outline-none transition-all focus:border-bp-primary focus:ring-2 focus:ring-bp-primary/40";

  return (
    <section id="contacto" data-testid="contact-section" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-6xl px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-start">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-sm text-bp-primary uppercase tracking-[0.2em] font-semibold">Contacto</span>
          <h2 className="font-heading text-3xl lg:text-5xl font-semibold tracking-tight mt-4">
            ¿Listo para empezar?
          </h2>
          <p className="mt-4 text-[#A3A3A3] leading-relaxed">
            Escríbenos y coordina tu activación o tu prueba gratuita. Respondemos rápido por tu canal favorito.
          </p>

          <div className="mt-8 space-y-3">
            <a
              href={waLink("Hola, quiero información sobre BrothersPlus TV.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="flex items-center gap-4 rounded-xl border border-bp-border bg-bp-surface p-4 transition-all hover:border-[#25D366]/50 hover:-translate-y-0.5"
            >
              <span className="grid place-items-center w-11 h-11 rounded-lg bg-[#25D366]/15 text-[#25D366]">
                <MessageCircle className="w-5 h-5" />
              </span>
              <div>
                <div className="text-white font-medium text-sm">WhatsApp</div>
                <div className="text-xs text-[#737373]">{CONTACT.whatsappDisplay}</div>
              </div>
            </a>
            <a
              href={tgLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-telegram"
              className="flex items-center gap-4 rounded-xl border border-bp-border bg-bp-surface p-4 transition-all hover:border-[#0088cc]/50 hover:-translate-y-0.5"
            >
              <span className="grid place-items-center w-11 h-11 rounded-lg bg-[#0088cc]/15 text-[#0088cc]">
                <Send className="w-5 h-5" />
              </span>
              <div>
                <div className="text-white font-medium text-sm">Telegram</div>
                <div className="text-xs text-[#737373]">{CONTACT.telegramDisplay}</div>
              </div>
            </a>
            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="contact-email"
              className="flex items-center gap-4 rounded-xl border border-bp-border bg-bp-surface p-4 transition-all hover:border-bp-primary/50 hover:-translate-y-0.5"
            >
              <span className="grid place-items-center w-11 h-11 rounded-lg bg-bp-primary/15 text-bp-primary">
                <Mail className="w-5 h-5" />
              </span>
              <div>
                <div className="text-white font-medium text-sm">Email</div>
                <div className="text-xs text-[#737373]">{CONTACT.email}</div>
              </div>
            </a>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="rounded-2xl border border-bp-border bg-bp-surface2 p-7 lg:p-9 space-y-4"
        >
          <input
            data-testid="contact-name"
            className={inputCls}
            placeholder="Tu nombre *"
            value={form.name}
            onChange={set("name")}
          />
          <input
            data-testid="contact-email-input"
            type="email"
            className={inputCls}
            placeholder="Tu email *"
            value={form.email}
            onChange={set("email")}
          />
          <select data-testid="contact-plan" className={inputCls} value={form.plan} onChange={set("plan")}>
            <option value="">Plan de interés (opcional)</option>
            {PLANS.map((p) => (
              <option key={p.name} value={p.name}>{p.name} — ${p.price}</option>
            ))}
          </select>
          <textarea
            data-testid="contact-message"
            className={`${inputCls} min-h-[120px] resize-none`}
            placeholder="Cuéntanos qué necesitas *"
            value={form.message}
            onChange={set("message")}
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-bp-primary px-6 py-3.5 font-semibold text-white transition-all duration-200 hover:bg-bp-primaryHover active:scale-95 hover:shadow-[0_0_24px_rgba(46,125,255,0.5)] disabled:opacity-60"
          >
            {loading ? "Enviando..." : "Enviar mensaje"} <Send className="w-4 h-4" />
          </button>
        </motion.form>
      </div>
    </section>
  );
};
